// V6-4-3 / V85-5-3 / ADR-0040: Blueprint Inventory -- evidence-supported census
//
// Queries evidence_connectors, discovered_tools, ci_check_submissions,
// and additional workspace data for a workspace and maps each to an
// InventoryObservation with the appropriate reference-architecture slot,
// claim, source provenance, and claim strength.
//
// Rules (R-85-05):
// - Populate only claims supported by observed evidence
// - Rich repos support >=8 distinct slots without an interview
// - Sparse/empty repos populate only supported slots; unknowns generate
//   targeted questions
// - Repository configuration alone does not prove effective operation
// - No fixed population quota -- claims come from evidence
// - Contradictory evidence stays qualified; unknowns feed questions
// - Repeat observations don't inflate slot count
import { CONNECTOR_MANIFESTS, collectorBackedKinds } from '../connectors/manifests.js';
import { validateLineageGraph } from './data-lineage-service.js';
/**
 * Connector kinds that have a shipped collector which actually reads from the
 * customer's system. ONLY these may produce a slot claim (V101-0-6): a
 * connector row whose kind has no collector (grafana, datadog, splunk,
 * newrelic, terraform_cloud, jira, servicenow, vanta, drata, slack, notion,
 * confluence, gitlab, bitbucket, cloudflare, ...) proves nothing, so its slot
 * stays unobserved and shows as needing a connection.
 *
 * Single source of truth until Track 1's connector manifest replaces it; a kind
 * is added here in the same change that ships its collector.
 */
export const COLLECTOR_BACKED_KINDS = new Set(collectorBackedKinds());
// ------------------------------------------------------------------
// Connector-kind to slot mapping (collector-backed kinds only)
// ------------------------------------------------------------------
//
// `aws` / `gcp` / `tenable` are collector-backed but claim no slot: the AWS
// read-back collects audit-trail, config, network, encryption-at-rest, backup
// and credential-rotation checks, none of which observe a key-management or
// secrets provider (the former `aws` -> secrets_kms "Key management via AWS
// KMS" claim was not supported by any collected check).
// ADR-0051 / V101-1-4: slots + claims now live on each kind's manifest.
const CONNECTOR_SLOT_MAP = Object.fromEntries(CONNECTOR_MANIFESTS.filter((m) => m.blueprint).map((m) => [m.kind, { slots: [...m.blueprint.slots], claims: [...m.blueprint.claims] }]));
/** Slots a connector kind may claim — empty for kinds with no collector. */
export function connectorSlotsForKind(kind) {
    return COLLECTOR_BACKED_KINDS.has(kind) ? [...(CONNECTOR_SLOT_MAP[kind]?.slots ?? [])] : [];
}
function matchDiscoveredTool(toolName, vendor) {
    const lower = (toolName + ' ' + (vendor ?? '')).toLowerCase();
    if (/\b(mlflow|wandb|weights.*biases|sagemaker|vertex|hugging\s?face|azure\s?ml)\b/.test(lower)) {
        return { slot_id: 'artifact_registry', claim: 'Model/artifact registry: ' + toolName, strength: 'inferred' };
    }
    if (/\b(promptfoo|ragas|deepeval|inspect)\b/.test(lower)) {
        return { slot_id: 'evaluation_harness', claim: 'Evaluation harness: ' + toolName, strength: 'inferred' };
    }
    if (/\b(opa|gatekeeper|kyverno|cedar|styra|conftest)\b/.test(lower)) {
        return { slot_id: 'policy_engine', claim: 'Policy engine: ' + toolName, strength: 'inferred' };
    }
    if (/\b(sigstore|cosign|notation|slsa|in-toto)\b/.test(lower)) {
        return { slot_id: 'signing_provenance', claim: 'Signing/provenance: ' + toolName, strength: 'inferred' };
    }
    if (/\b(langfuse|arize|otel|opentelemetry|datadog|newrelic|grafana)\b/.test(lower)) {
        return { slot_id: 'telemetry', claim: 'Telemetry/observability: ' + toolName, strength: 'inferred' };
    }
    if (/\b(zenity|pillar|arthur|witnessai)\b/.test(lower)) {
        return { slot_id: 'runtime_enforcement', claim: 'Runtime enforcement: ' + toolName, strength: 'inferred' };
    }
    if (/\b(jira|servicenow|archer|vanta|drata|anecdotes)\b/.test(lower)) {
        return { slot_id: 'grc_platform', claim: 'GRC platform: ' + toolName, strength: 'inferred' };
    }
    if (/\b(purview|collibra|atlan|openmetadata)\b/.test(lower)) {
        return { slot_id: 'data_governance', claim: 'Data governance: ' + toolName, strength: 'inferred' };
    }
    if (/\b(vault|kms|key\s?vault)\b/.test(lower)) {
        return { slot_id: 'secrets_kms', claim: 'Secrets/KMS: ' + toolName, strength: 'inferred' };
    }
    if (/\b(snyk|trivy|grype|anchore|dependency.track|syft)\b/.test(lower)) {
        return { slot_id: 'sbom_inventory', claim: 'SBOM/component scanning: ' + toolName, strength: 'inferred' };
    }
    if (/\b(okta|entra|auth0|onelogin|ping\s?identity)\b/.test(lower)) {
        return { slot_id: 'identity', claim: 'Identity provider: ' + toolName, strength: 'inferred' };
    }
    if (/\b(zylo|productiv|torii|nudge\s?security)\b/.test(lower)) {
        return { slot_id: 'ai_discovery', claim: 'AI discovery tool: ' + toolName, strength: 'inferred' };
    }
    // Default: classified as AI discovery (the fact that a tool was discovered)
    return { slot_id: 'ai_discovery', claim: 'Discovered AI tool: ' + toolName, strength: 'inferred' };
}
const CI_TOOL_PATTERNS = [
    { pattern: /promptfoo|ragas|deepeval|inspect/i, slot_id: 'evaluation_harness', claimPrefix: 'CI eval tool' },
    { pattern: /cosign|sigstore|slsa|in-toto|notation/i, slot_id: 'signing_provenance', claimPrefix: 'CI signing tool' },
    { pattern: /cyclonedx|spdx|syft|trivy|grype|snyk/i, slot_id: 'sbom_inventory', claimPrefix: 'CI SBOM tool' },
    { pattern: /opa|kyverno|cedar|conftest/i, slot_id: 'policy_engine', claimPrefix: 'CI policy check' },
];
function matchCiTool(toolName) {
    for (const { pattern, slot_id, claimPrefix } of CI_TOOL_PATTERNS) {
        if (pattern.test(toolName)) {
            return { slot_id, claim: claimPrefix + ': ' + toolName };
        }
    }
    return null;
}
// ------------------------------------------------------------------
// Deduplication: repeat observations don't inflate slot count
// ------------------------------------------------------------------
function deduplicateObservations(observations) {
    const STRENGTH_RANK = {
        directly_observed: 3,
        inferred: 2,
        user_supplied: 1,
    };
    const seen = new Map();
    for (const obs of observations) {
        const key = obs.slot_id + ':' + obs.source_reference;
        const existing = seen.get(key);
        if (!existing || STRENGTH_RANK[obs.claim_strength] > STRENGTH_RANK[existing.claim_strength]) {
            seen.set(key, obs);
        }
    }
    return Array.from(seen.values());
}
// ------------------------------------------------------------------
// All reference-architecture slot IDs for question generation
// ------------------------------------------------------------------
const ALL_SLOT_IDS = [
    'source_control', 'ci_cd', 'policy_engine', 'signing_provenance',
    'artifact_registry', 'evaluation_harness', 'telemetry', 'secrets_kms',
    'identity', 'runtime_enforcement', 'grc_platform', 'data_governance',
    'human_review', 'sbom_inventory', 'ai_discovery',
];
const SLOT_QUESTIONS = {
    source_control: { question: 'What source control system does your team use (GitHub, GitLab, etc.)?', reason: 'No source control connector detected' },
    ci_cd: { question: 'What CI/CD pipeline runs your builds and deployments?', reason: 'No CI/CD activity or connector detected' },
    policy_engine: { question: 'Do you use a policy engine (OPA, Kyverno, Cedar) to gate deployments?', reason: 'No policy engine detected in connectors, tools, or CI' },
    signing_provenance: { question: 'Do you sign artifacts or generate SLSA provenance in your pipeline?', reason: 'No signing or provenance tooling detected' },
    artifact_registry: { question: 'Where do you store versioned models and ML artifacts?', reason: 'No model/artifact registry detected' },
    evaluation_harness: { question: 'How do you evaluate AI model performance before release?', reason: 'No evaluation harness detected in CI or tools' },
    telemetry: { question: 'What observability stack monitors your AI systems in production?', reason: 'No telemetry/monitoring connector or tool detected' },
    secrets_kms: { question: 'How are signing keys and API credentials managed (Vault, KMS, etc.)?', reason: 'No secrets/KMS provider detected' },
    identity: { question: 'What identity provider manages SSO and user access?', reason: 'No identity connector detected' },
    runtime_enforcement: { question: 'Do you use a runtime enforcement layer to gate AI inference requests?', reason: 'No runtime enforcement tool detected' },
    grc_platform: { question: 'What GRC or compliance platform tracks findings and evidence?', reason: 'No GRC platform connector detected' },
    data_governance: { question: 'How do you track training data provenance and classification?', reason: 'No data governance tool or lineage graph detected' },
    human_review: { question: 'What workflow manages human sign-off for AI system releases?', reason: 'No human review/approval workflow detected' },
    sbom_inventory: { question: 'Do you generate SBOMs (CycloneDX/SPDX) for your AI system components?', reason: 'No SBOM tooling detected in CI or connectors' },
    ai_discovery: { question: 'How do you discover which AI tools are in use across the organisation?', reason: 'No AI discovery signals detected' },
};
// ------------------------------------------------------------------
// Main function
// ------------------------------------------------------------------
export async function buildInventoryFromConnectors(db, workspaceId) {
    const observations = [];
    const items = [];
    const now = new Date().toISOString();
    // ---- 1. Evidence connectors ------------------------------------------
    const connectors = await db.query(`SELECT connector_id, kind, status
       FROM evidence_connectors
      WHERE workspace_id = ? AND status = 'active'`, [workspaceId]);
    for (const c of connectors.results) {
        // V101-0-6: only collector-backed kinds may claim a slot.
        if (!COLLECTOR_BACKED_KINDS.has(c.kind))
            continue;
        const mapping = CONNECTOR_SLOT_MAP[c.kind];
        if (!mapping)
            continue;
        for (let i = 0; i < mapping.slots.length; i++) {
            const slot_id = mapping.slots[i];
            const claim = mapping.claims[i] || ('Active ' + c.kind + ' connector');
            observations.push({
                slot_id,
                claim,
                source_type: 'connector',
                source_reference: 'connector:' + c.connector_id,
                observation_time: now,
                claim_strength: 'directly_observed',
                stale_after_days: 30,
            });
            items.push({
                slot_id,
                detected_by: 'connector',
                evidence_ref: 'connector:' + c.connector_id,
                fit: 'native',
                confidence: 0.8,
            });
        }
    }
    // ---- 2. Discovered tools ---------------------------------------------
    const tools = await db.query(`SELECT t.tool_id, t.name AS tool_name, v.name AS vendor, t.status, t.last_seen
       FROM discovered_tools t
       LEFT JOIN vendors v ON v.vendor_id = t.vendor_id
      WHERE t.workspace_id = ?
        AND t.status IN ('sanctioned', 'new')`, [workspaceId]);
    for (const t of tools.results) {
        const match = matchDiscoveredTool(t.tool_name, t.vendor);
        observations.push({
            slot_id: match.slot_id,
            claim: match.claim,
            source_type: 'discovered_tool',
            source_reference: 'discovered_tool:' + t.tool_id,
            observation_time: t.last_seen || now,
            claim_strength: match.strength,
            stale_after_days: 90,
        });
        items.push({
            slot_id: match.slot_id,
            detected_by: 'connector',
            evidence_ref: 'discovered_tool:' + t.tool_id,
            fit: t.status === 'sanctioned' ? 'native' : 'partial',
            confidence: t.status === 'sanctioned' ? 0.7 : 0.4,
        });
    }
    // ---- 3. CI check submissions -----------------------------------------
    const ciChecks = await db.query(`SELECT submission_id, tool AS tool_name, submitted_at
       FROM ci_check_submissions
      WHERE workspace_id = ?
      ORDER BY submitted_at DESC
      LIMIT 100`, [workspaceId]);
    if (ciChecks.results.length > 0) {
        const latestSubmission = ciChecks.results[0];
        observations.push({
            slot_id: 'ci_cd',
            claim: ciChecks.results.length + ' CI check submissions observed',
            source_type: 'ci_submission',
            source_reference: 'ci_submissions:' + ciChecks.results.length,
            observation_time: latestSubmission.submitted_at || now,
            claim_strength: 'directly_observed',
            stale_after_days: 14,
        });
        items.push({
            slot_id: 'ci_cd',
            detected_by: 'ci_activity',
            evidence_ref: 'ci_submissions:' + ciChecks.results.length,
            fit: 'native',
            confidence: 0.9,
        });
        // Categorize CI tools into specific slots -- deduplicate per slot
        const ciSlotSeen = new Set();
        for (const c of ciChecks.results) {
            const match = matchCiTool(c.tool_name);
            if (match && !ciSlotSeen.has(match.slot_id)) {
                ciSlotSeen.add(match.slot_id);
                observations.push({
                    slot_id: match.slot_id,
                    claim: match.claim,
                    source_type: 'ci_submission',
                    source_reference: 'ci_tool:' + c.submission_id,
                    observation_time: c.submitted_at || now,
                    claim_strength: 'directly_observed',
                    stale_after_days: 14,
                });
                items.push({
                    slot_id: match.slot_id,
                    detected_by: 'ci_activity',
                    evidence_ref: 'ci_tool:' + c.tool_name,
                    fit: 'native',
                    confidence: 0.85,
                });
            }
        }
    }
    // ---- 4. BOM snapshots (SBOM inventory) --------------------------------
    const bomCount = await db.query(`SELECT COUNT(*) AS n, MAX(published_at) AS latest
       FROM ai_system_bom_snapshots WHERE workspace_id = ?`, [workspaceId]);
    const bomN = bomCount.results[0]?.n ?? 0;
    if (bomN > 0) {
        observations.push({
            slot_id: 'sbom_inventory',
            claim: bomN + ' BOM snapshot(s) ingested',
            source_type: 'bom_snapshot',
            source_reference: 'bom_snapshots:' + bomN,
            observation_time: bomCount.results[0]?.latest || now,
            claim_strength: 'directly_observed',
            stale_after_days: 30,
        });
        items.push({
            slot_id: 'sbom_inventory',
            detected_by: 'ci_activity',
            evidence_ref: 'bom_snapshots:' + bomN,
            fit: 'native',
            confidence: 0.9,
        });
    }
    // ---- 5. Data lineage graphs (data governance) -------------------------
    const lineage = await db.query(`SELECT l.system_id, l.graph_json, l.updated_at FROM system_data_lineage l
       JOIN ai_systems s ON s.system_id = l.system_id AND s.workspace_id = l.workspace_id
      WHERE l.workspace_id = ?`, [workspaceId]);
    for (const row of lineage.results) {
        try {
            const graph = validateLineageGraph(JSON.parse(row.graph_json));
            if (graph.nodes.some(n => n.type === 'dataset')) {
                observations.push({
                    slot_id: 'data_governance',
                    claim: 'Data lineage graph with dataset nodes for system ' + row.system_id,
                    source_type: 'data_lineage',
                    source_reference: 'lineage:' + row.system_id,
                    observation_time: row.updated_at || now,
                    claim_strength: 'inferred',
                    stale_after_days: 60,
                });
                items.push({
                    slot_id: 'data_governance',
                    detected_by: 'interview',
                    evidence_ref: 'lineage:' + row.system_id,
                    fit: 'substitute',
                    confidence: 0.7,
                });
            }
        }
        catch {
            // Invalid lineage graph -- skip, do not invent coverage
        }
    }
    // ---- 6. Workspace-level AI discovery signals --------------------------
    if (tools.results.length > 0) {
        observations.push({
            slot_id: 'ai_discovery',
            claim: tools.results.length + ' AI tool(s) discovered in workspace',
            source_type: 'workspace_config',
            source_reference: 'discovered_tools:' + tools.results.length,
            observation_time: now,
            claim_strength: 'directly_observed',
            stale_after_days: 30,
        });
        items.push({
            slot_id: 'ai_discovery',
            detected_by: 'connector',
            evidence_ref: 'discovered_tools:' + tools.results.length,
            fit: 'native',
            confidence: 0.8,
        });
    }
    // ---- 7. Signing settings → secrets_kms ---------------------------------
    const sigSettings = await db.query(`SELECT workspace_id, topology FROM workspace_signing_settings
      WHERE workspace_id = ?`, [workspaceId]);
    for (const ss of sigSettings.results) {
        const kmsTopologies = ['aws_kms', 'azure_keyvault', 'gcp_kms', 'vault_transit'];
        if (kmsTopologies.includes(ss.topology)) {
            observations.push({
                slot_id: 'secrets_kms',
                claim: 'KMS-backed signing via ' + ss.topology.replace(/_/g, ' '),
                source_type: 'workspace_config',
                source_reference: 'signing_settings:' + ss.workspace_id,
                observation_time: now,
                claim_strength: 'directly_observed',
                stale_after_days: 90,
            });
            items.push({
                slot_id: 'secrets_kms',
                detected_by: 'connector',
                evidence_ref: 'signing_settings:' + ss.topology,
                fit: 'native',
                confidence: 0.9,
            });
        }
        else if (ss.topology === 'pull_sign_push') {
            // pull_sign_push implies external key management, but we don't know which
            observations.push({
                slot_id: 'secrets_kms',
                claim: 'External key management inferred from pull-sign-push topology',
                source_type: 'workspace_config',
                source_reference: 'signing_settings:' + ss.workspace_id,
                observation_time: now,
                claim_strength: 'inferred',
                stale_after_days: 90,
            });
            items.push({
                slot_id: 'secrets_kms',
                detected_by: 'connector',
                evidence_ref: 'signing_settings:' + ss.topology,
                fit: 'partial',
                confidence: 0.5,
            });
        }
    }
    // ---- 8. Entry review states → human_review ----------------------------
    const reviewStates = await db.query(`SELECT COUNT(*) AS n, MAX(ers.updated_at) AS latest
       FROM entry_review_states ers
       JOIN ai_disclosure_versions v ON v.version_id = ers.version_id
       JOIN ai_disclosures d ON d.disclosure_id = v.disclosure_id
      WHERE d.workspace_id = ?
        AND ers.state IN ('in_review', 'changes_requested', 'signed', 'released')`, [workspaceId]);
    const reviewN = reviewStates.results[0]?.n ?? 0;
    if (reviewN > 0) {
        observations.push({
            slot_id: 'human_review',
            claim: reviewN + ' entry review(s) with active human sign-off',
            source_type: 'workspace_config',
            source_reference: 'entry_review_states:' + reviewN,
            observation_time: reviewStates.results[0]?.latest || now,
            claim_strength: 'directly_observed',
            stale_after_days: 30,
        });
        items.push({
            slot_id: 'human_review',
            detected_by: 'connector',
            evidence_ref: 'entry_review_states:' + reviewN,
            fit: 'native',
            confidence: 0.85,
        });
    }
    // ---- 9. Control checks → runtime_enforcement --------------------------
    const enforceChecks = await db.query(`SELECT COUNT(*) AS n, MAX(observed_at) AS latest
       FROM control_checks
      WHERE workspace_id = ?`, [workspaceId]);
    const enforceN = enforceChecks.results[0]?.n ?? 0;
    if (enforceN > 0) {
        observations.push({
            slot_id: 'runtime_enforcement',
            claim: enforceN + ' control check(s) observed — active enforcement pipeline',
            source_type: 'workspace_config',
            source_reference: 'control_checks:' + enforceN,
            observation_time: enforceChecks.results[0]?.latest || now,
            claim_strength: 'directly_observed',
            stale_after_days: 14,
        });
        items.push({
            slot_id: 'runtime_enforcement',
            detected_by: 'ci_activity',
            evidence_ref: 'control_checks:' + enforceN,
            fit: 'native',
            confidence: 0.85,
        });
    }
    // ---- 10. Discovery signal aggregates → ai_discovery -------------------
    const signalAggs = await db.query(`SELECT COUNT(*) AS n, MAX(period_end) AS latest
       FROM discovery_signal_aggregates
      WHERE workspace_id = ?`, [workspaceId]);
    const aggN = signalAggs.results[0]?.n ?? 0;
    if (aggN > 0) {
        observations.push({
            slot_id: 'ai_discovery',
            claim: aggN + ' discovery signal aggregate(s) from IdP/CASB/network telemetry',
            source_type: 'workspace_config',
            source_reference: 'discovery_signal_aggregates:' + aggN,
            observation_time: signalAggs.results[0]?.latest || now,
            claim_strength: 'directly_observed',
            stale_after_days: 30,
        });
        items.push({
            slot_id: 'ai_discovery',
            detected_by: 'connector',
            evidence_ref: 'discovery_signal_aggregates:' + aggN,
            fit: 'native',
            confidence: 0.9,
        });
    }
    // ---- Deduplicate observations (R-85-05: repeats don't inflate) --------
    const dedupedObservations = deduplicateObservations(observations);
    // ---- Generate targeted questions for unobserved slots -----------------
    const observedSlots = new Set(dedupedObservations.map(o => o.slot_id));
    const questions = [];
    for (const slotId of ALL_SLOT_IDS) {
        if (!observedSlots.has(slotId)) {
            const q = SLOT_QUESTIONS[slotId];
            if (q) {
                questions.push({ slot_id: slotId, ...q });
            }
        }
    }
    return {
        items,
        observations: dedupedObservations,
        questions,
        observed_slot_ids: [...observedSlots],
        connectors_scanned: connectors.results.length,
        discovered_tools_scanned: tools.results.length,
        ci_submissions_scanned: ciChecks.results.length,
    };
}
//# sourceMappingURL=blueprint-inventory-service.js.map